import React from 'react';
import { Link } from '@inertiajs/react';
import { Seo, faqPage } from '~/marketing/Seo';
import { Button } from '~/marketing/ui';
import { routes } from '~/marketing/routes';
import { withMarketingLayout } from '~/marketing/MarketingLayout';
import { faq } from '~/marketing/faqData';

/* The questions and the markup both come from `faqData.js`. FAQPage structured
   data has to describe content visible on the page, so rendering one array and
   serialising the same array is what makes the markup honest rather than a
   claim about content that is not there. `structured_data_test` checks every
   marked-up answer appears in the response body.

   Plain headings and paragraphs rather than an accordion: an answer hidden
   behind a click is still in the DOM and still gets quoted, but the whole point
   of this page is that somebody can read it, and eleven questions is a short
   page rather than a long one. */

const sec = { display: 'grid', gap: 'var(--space-6)' };
const item = { display: 'grid', gap: 'var(--space-3)', maxWidth: '72ch' };

export default function Faq() {
  return (
    <>
      <Seo
        title="FAQ: AI search optimization for local businesses"
        description="How AI assistants recommend local businesses and how Spruce measures it: which AI assistants, how often, what the number counts, and what plans cost."
        path={routes.faq}
        schema={[ faqPage ]}
      />

      <main
        className="sp-page"
        style={{ paddingBlock: 'clamp(40px,5vw,80px)', display: 'grid', gap: 'var(--space-12)' }}
      >
        <header style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: '72ch' }}>
          <span className="sp-label">FAQ</span>
          {/* The size every other marketing page's title uses. It asked for
              `--fs-display-3` until 2026-10-23, a token the design system
              never defined, so the title rendered at body size, 15px, under
              questions set larger than it. `css_variables_test` holds it now. */}
          <h1 style={{ fontSize: 'var(--fs-display-2)', lineHeight: 1.06, margin: 0 }}>
            Frequently asked questions
          </h1>
          <p style={{ fontSize: 'var(--fs-body-lg)', color: 'var(--text-body)', margin: 0 }}>
            How AI assistants recommend local businesses, how we measure it, and how
            plans work. The <Link href={routes.methodology}>methodology page</Link>{' '}
            covers the measurement in more detail.
          </p>
        </header>

        <section style={sec}>
          {faq.map(({ q, a }) => (
            <div key={q} style={item}>
              <h2 style={{ fontSize: 'var(--fs-h3)', margin: 0 }}>{q}</h2>
              {a.map((paragraph) => (
                <p key={paragraph} style={{ color: 'var(--text-body)', margin: 0 }}>
                  {paragraph}
                </p>
              ))}
            </div>
          ))}
        </section>

        <section
          style={{
            display: 'grid',
            gap: 'var(--space-4)',
            borderTop: '1px solid var(--border-subtle)',
            paddingTop: 'var(--space-8)',
            maxWidth: '72ch',
          }}
        >
          <h2 style={{ fontSize: 'var(--fs-h3)', margin: 0 }}>
            Still deciding? Ask the AI assistants about your own business.
          </h2>
          <p style={{ color: 'var(--text-body)', margin: 0 }}>
            One question, five AI assistants, the report emailed to you. No credit card.
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap', alignItems: 'center' }}>
            <Button size="lg" href={routes.grader}>Check your visibility</Button>
            <Link href={routes.pricing}>See the plans</Link>
          </div>
        </section>
      </main>
    </>
  );
}

Faq.layout = withMarketingLayout;
